import { useEffect } from 'react';

import { useAppDispatch } from '@fd/customHooks/useAppDispatch';
import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { useQuery } from '@tanstack/react-query';
import { getTranslate } from 'react-localize-redux';
import { useHistory } from 'react-router-dom';

import { appsActions } from '../actions/apps.actions';
import {
  getOnboardingV3Status,
  getOnboardingV3StatusKey,
} from '../components/OnboardingV3/onboardingV3.service';
import { brandService } from '../components/Settings/Brands/brands.services';
import { notifyError } from '../layouts/Notify/actions';
import { flagService } from '../services';
import { isOrgInPathName } from './utils';

const RedirectOrgToBrand = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const isFlagOn = useAppSelector((state) => flagService.isFlagOn(state, 'redirectOrgToBrand'));
  const translate = useAppSelector((state) => getTranslate(state));

  const locationUrl = new URL(window.location.href);
  let orgId = '';

  if (isOrgInPathName(locationUrl.pathname)) {
    orgId = locationUrl.pathname.slice(1);
  }

  const {
    data: brandsResponse,
    isFetched,
    isLoading,
    error: brandsForOrgError,
  } = useQuery({
    queryKey: [brandService.getBrandsForOrgKey, orgId],
    queryFn: () => brandService.getBrandsForOrg(orgId),
    enabled: !!orgId && isFlagOn,
  });

  const firstBrandId = brandsResponse?.data?.data?.length
    ? brandsResponse.data.data[0].brandId
    : undefined;

  const { data: onboardingStatusData, isFetched: isOnboardingStatusFetched } = useQuery({
    queryKey: [orgId, getOnboardingV3StatusKey],
    queryFn: () => getOnboardingV3Status(orgId),
    enabled: !!firstBrandId && isFetched && !isLoading,
  });

  useEffect(() => {
    if (!orgId || !isFetched || isLoading) return;

    if (!firstBrandId) {
      if (!brandsForOrgError) {
        dispatch(
          notifyError({
            message: translate('No_brands_found', { orgId }) as string,
            translate: false,
          })
        );
      }
      return;
    }

    if (!isOnboardingStatusFetched) return;

    const isInProgress = onboardingStatusData?.data?.status === 'in_progress';
    (async () => {
      await dispatch(appsActions.setCurrentApp(firstBrandId));
      if (isInProgress) {
        history.replace(`/${firstBrandId}/setup`);
      } else {
        history.replace(`/${firstBrandId}/home`);
      }
    })();
  }, [
    orgId,
    firstBrandId,
    isFetched,
    isLoading,
    brandsForOrgError,
    isOnboardingStatusFetched,
    onboardingStatusData,
  ]);

  useEffect(() => {
    const brandsForOrgErrorMessage = brandsForOrgError?.message;
    if (brandsForOrgErrorMessage) {
      dispatch(notifyError({ message: brandsForOrgErrorMessage, translate: false }));
    }
  }, [brandsForOrgError]);

  return null;
};

export default RedirectOrgToBrand;
